<!--
  StopHold — the stop-hold record.

  Spec, Data model: repeating events of `event_no`, `time_stopped`, `reason`,
  `corrective_action`, `additional_test_required` (Y/N), `restart_time`,
  `qc_acceptance_notes`.

  Two things make this screen matter rather than merely exist:

  * An event with no restart time is OPEN, and says so loudly. Appendix B item
    11 is "all hold/stop events documented and closed", and the spec's close
    rule lets a failed Hold point be resolved by being "paired with a stop-hold
    event" — so whether an event is still open is the fact this screen has to
    make impossible to miss.

  * Adding an event stamps the stop time immediately. Production has already
    stopped by the time anyone reaches for a phone; the time is the one field
    that must not be reconstructed from memory.

  Delete is a two-tap inline confirm. No modal dialog can trap a user mid-pour.
-->
<script lang="ts">
  import type {
    CreateStopHoldEventRequest,
    SqlBool,
    StopHoldEvent,
    UpdateStopHoldEventRequest
  } from '../../lib/types';
  import YesNoField from '../../lib/fields/YesNoField.svelte';
  import NotesField from '../../lib/fields/NotesField.svelte';
  import { clearDraft, loadDraft, saveDraft } from '../../lib/fields/local-draft';

  interface Props {
    pourId: number;
    /** Server rows, any order; rendered by event_no. */
    events: StopHoldEvent[];
    readonly?: boolean;
    onCreate?: (body: CreateStopHoldEventRequest) => Promise<StopHoldEvent | void>;
    onUpdate?: (eventId: number, body: UpdateStopHoldEventRequest) => Promise<void>;
    onDelete?: (eventId: number) => Promise<void>;
  }

  let { pourId, events, readonly = false, onCreate, onUpdate, onDelete }: Props = $props();

  /* ------------------------------------------------------------------ draft */

  type Editable = Omit<
    StopHoldEvent,
    'id' | 'pour_id' | 'event_no' | 'created_at' | 'updated_at'
  >;
  type Draft = Partial<Editable>;

  const DRAFT_KEY = `stop-hold.${pourId}`;

  interface DraftState {
    values: Record<number, Draft>;
    dirty: Record<number, string[]>;
  }

  const restored = loadDraft<DraftState>(DRAFT_KEY);
  let values = $state<Record<number, Draft>>(restored?.values ?? {});
  let dirty = $state<Record<number, string[]>>(restored?.dirty ?? {});

  let saving = $state<Record<number, boolean>>({});
  let errors = $state<Record<number, string | null>>({});
  let confirmingDelete = $state<number | null>(null);
  let confirmTimer: ReturnType<typeof setTimeout> | null = null;
  let adding = $state(false);
  let addError = $state<string | null>(null);

  $effect(() => {
    const snapshot: DraftState = { values: $state.snapshot(values), dirty: $state.snapshot(dirty) };
    if (Object.keys(snapshot.values).length === 0) clearDraft(DRAFT_KEY);
    else saveDraft(DRAFT_KEY, snapshot);
  });

  let rows = $derived(
    [...events]
      .sort((a, b) => a.event_no - b.event_no)
      .map((base) => ({ base, view: { ...base, ...(values[base.id] ?? {}) } as StopHoldEvent }))
  );

  let openCount = $derived(rows.filter((r) => !r.view.restart_time).length);

  function isDirty(id: number): boolean {
    return (dirty[id]?.length ?? 0) > 0;
  }

  function set<K extends keyof Editable>(id: number, key: K, v: Editable[K]) {
    if (readonly) return;
    if (!values[id]) values[id] = {};
    values[id][key] = v;
    const keys = dirty[id] ?? [];
    if (!keys.includes(key as string)) dirty[id] = [...keys, key as string];
  }

  /** Flush every pending field of one event in a single PATCH. */
  async function flush(id: number) {
    const pending = dirty[id];
    if (readonly || !pending || pending.length === 0 || saving[id]) return;

    const draft = values[id] ?? {};
    const patch: Record<string, unknown> = {};
    for (const key of pending) patch[key] = (draft as Record<string, unknown>)[key];

    saving[id] = true;
    errors[id] = null;
    try {
      await onUpdate?.(id, patch as UpdateStopHoldEventRequest);
      const still = (dirty[id] ?? []).filter(
        (k) => (values[id] as Record<string, unknown>)?.[k] !== patch[k]
      );
      if (still.length === 0) delete dirty[id];
      else dirty[id] = still;
      prune(id);
    } catch (e) {
      // The draft is never discarded because a request failed.
      errors[id] = e instanceof Error ? e.message : 'Could not save. Tap retry.';
    } finally {
      saving[id] = false;
    }
  }

  function prune(id: number) {
    const base = events.find((t) => t.id === id);
    const draft = values[id];
    if (!base || !draft) return;
    for (const k of Object.keys(draft) as (keyof Editable)[]) {
      if (dirty[id]?.includes(k as string)) continue;
      if ((base as unknown as Record<string, unknown>)[k as string] === draft[k]) delete draft[k];
    }
    if (Object.keys(draft).length === 0) delete values[id];
  }

  /* -------------------------------------------------------------- add/time */

  function nowHHMM(): string {
    const d = new Date();
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  }

  function timeValue(v: string | null): string {
    if (!v) return '';
    if (/^\d{2}:\d{2}$/.test(v)) return v;
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return '';
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  }

  async function addEvent() {
    if (readonly || adding) return;
    adding = true;
    addError = null;
    try {
      // Production has already stopped; stamp the time now rather than ask a
      // QC person to reconstruct it later.
      await onCreate?.({ time_stopped: nowHHMM() });
    } catch (e) {
      addError = e instanceof Error ? e.message : 'Could not add an event. Tap to retry.';
    } finally {
      adding = false;
    }
  }

  function stampRestart(id: number) {
    if (readonly) return;
    set(id, 'restart_time', nowHHMM());
    flush(id);
  }

  function askDelete(id: number) {
    if (confirmTimer) clearTimeout(confirmTimer);
    confirmingDelete = id;
    confirmTimer = setTimeout(() => (confirmingDelete = null), 4000);
  }

  async function doDelete(id: number) {
    if (confirmTimer) clearTimeout(confirmTimer);
    confirmingDelete = null;
    saving[id] = true;
    errors[id] = null;
    try {
      await onDelete?.(id);
      delete values[id];
      delete dirty[id];
    } catch (e) {
      errors[id] = e instanceof Error ? e.message : 'Could not delete.';
    } finally {
      saving[id] = false;
    }
  }
</script>

<section class="sh" aria-label="Stop and hold record">
  <header class="head">
    <h2>Stop / hold record</h2>
    <span class="count">{rows.length}</span>
    {#if openCount > 0}
      <span class="openflag">{openCount} open</span>
    {/if}
    {#if !readonly}
      <button type="button" class="add" onclick={addEvent} disabled={adding}>
        {adding ? 'Adding…' : '+ Record stop'}
      </button>
    {/if}
  </header>

  {#if addError}
    <p class="banner bad" role="alert">
      {addError}
      <button type="button" class="retry" onclick={addEvent}>Retry</button>
    </p>
  {/if}

  {#if rows.length === 0}
    <p class="empty">No stop or hold events recorded for this pour.</p>
  {/if}

  <ol class="rows">
    {#each rows as { base, view } (base.id)}
      {@const open = !view.restart_time}
      <li class="card" class:open={open} class:unsaved={isDirty(base.id)}>
        <div class="cardhead">
          <span class="no">Event {base.event_no}</span>
          <span class="stopped">Stopped {timeValue(view.time_stopped) || '--:--'}</span>
          <span class="status" class:open={open}
            >{open ? 'OPEN' : `Restarted ${timeValue(view.restart_time)}`}</span
          >
          <span class="state" aria-live="polite">
            {#if saving[base.id]}
              <span class="pill saving">Saving…</span>
            {:else if isDirty(base.id)}
              <span class="pill dirty">Unsaved</span>
              <button type="button" class="retry" onclick={() => flush(base.id)}>Save</button>
            {/if}
          </span>
        </div>

        {#if errors[base.id]}
          <p class="banner bad" role="alert">
            {errors[base.id]}
            <button type="button" class="retry" onclick={() => flush(base.id)}>Retry</button>
          </p>
        {/if}

        <div class="body">
          <div class="times">
            <label class="text">
              <span>Time stopped</span>
              <input
                type="time"
                disabled={readonly}
                value={timeValue(view.time_stopped)}
                oninput={(e) =>
                  set(base.id, 'time_stopped', e.currentTarget.value === '' ? null : e.currentTarget.value)}
                onblur={() => flush(base.id)}
              />
            </label>

            <!-- Not a <label> wrapper: a <button> may not sit inside one. -->
            <div class="text">
              <label for="restart-{base.id}">Restart time</label>
              <span class="withbtn">
                <input
                  id="restart-{base.id}"
                  type="time"
                  disabled={readonly}
                  value={timeValue(view.restart_time)}
                  oninput={(e) =>
                    set(base.id, 'restart_time', e.currentTarget.value === '' ? null : e.currentTarget.value)}
                  onblur={() => flush(base.id)}
                />
                {#if !readonly}
                  <button type="button" class="quick" onclick={() => stampRestart(base.id)}>Now</button>
                {/if}
              </span>
            </div>
          </div>

          <NotesField
            label="Reason for stop / hold"
            value={view.reason}
            rows={2}
            disabled={readonly}
            onValue={(v) => set(base.id, 'reason', v)}
            onCommit={() => flush(base.id)}
          />

          <NotesField
            label="Corrective action"
            value={view.corrective_action}
            rows={2}
            disabled={readonly}
            onValue={(v) => set(base.id, 'corrective_action', v)}
            onCommit={() => flush(base.id)}
          />

          <YesNoField
            label="Additional testing required"
            value={view.additional_test_required as SqlBool | null}
            disabled={readonly}
            onValue={(v) => {
              set(base.id, 'additional_test_required', v as SqlBool | null);
              flush(base.id);
            }}
          />

          {#if view.additional_test_required === 1}
            <p class="advice">
              Record the additional chute test under Chute tests, test point
              <strong>After stop / restart</strong>.
            </p>
          {/if}

          <NotesField
            label="QC acceptance notes"
            value={view.qc_acceptance_notes}
            rows={2}
            disabled={readonly}
            onValue={(v) => set(base.id, 'qc_acceptance_notes', v)}
            onCommit={() => flush(base.id)}
          />

          {#if open}
            <p class="advice warn">
              This event is open. It stays open until a restart time is recorded.
            </p>
          {/if}

          {#if !readonly}
            <div class="rowactions">
              {#if confirmingDelete === base.id}
                <button type="button" class="danger" onclick={() => doDelete(base.id)}>
                  Confirm delete event {base.event_no}
                </button>
                <button type="button" class="quiet" onclick={() => (confirmingDelete = null)}>
                  Keep
                </button>
              {:else}
                <button type="button" class="quiet" onclick={() => askDelete(base.id)}>
                  Delete event
                </button>
              {/if}
            </div>
          {/if}
        </div>
      </li>
    {/each}
  </ol>
</section>

<style>
  .sh {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .head {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-wrap: wrap;
  }

  h2 {
    margin: 0;
    font-size: 1.0625rem;
    font-weight: 800;
  }

  .count {
    font-size: 0.8125rem;
    font-weight: 800;
    padding: 0.1rem 0.45rem;
    border-radius: 999px;
    background: var(--surface-muted, #eef1f4);
    color: var(--fg-muted, #3f4750);
  }

  .openflag {
    font-size: 0.75rem;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    padding: 0.15rem 0.5rem;
    border-radius: 999px;
    background: var(--bad-bg, #fdecea);
    color: var(--bad, #b3261e);
  }

  .add {
    margin-left: auto;
    min-height: 3rem;
    padding: 0 1rem;
    font-size: 1rem;
    font-weight: 800;
    border: 2px solid var(--bad, #b3261e);
    border-radius: 0.5rem;
    background: var(--bad, #b3261e);
    color: #fff;
    cursor: pointer;
    touch-action: manipulation;
  }

  .add:disabled {
    opacity: 0.6;
  }

  .empty {
    margin: 0;
    padding: 1rem;
    border: 2px dashed var(--border, #9aa3ad);
    border-radius: 0.5rem;
    color: var(--fg-muted, #3f4750);
    font-size: 0.9375rem;
  }

  .rows {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 0.625rem;
  }

  .card {
    border: 2px solid var(--border, #9aa3ad);
    border-radius: 0.625rem;
    background: var(--surface, #fff);
    overflow: hidden;
  }

  .card.open {
    border-color: var(--bad, #b3261e);
    border-left-width: 8px;
  }

  .card.unsaved {
    border-color: var(--warn, #a35a00);
  }

  .cardhead {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-wrap: wrap;
    padding: 0.625rem;
    background: var(--surface-muted, #eef1f4);
  }

  .no {
    font-weight: 900;
  }

  .stopped {
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    color: var(--fg-muted, #3f4750);
  }

  .status {
    font-size: 0.75rem;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    padding: 0.15rem 0.5rem;
    border-radius: 999px;
    background: var(--good-bg, #e7f6ed);
    color: var(--good, #1a7f45);
  }

  .status.open {
    background: var(--bad, #b3261e);
    color: #fff;
  }

  .state {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    margin-left: auto;
  }

  .pill {
    font-size: 0.75rem;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    padding: 0.15rem 0.45rem;
    border-radius: 999px;
  }

  .pill.dirty {
    background: var(--warn-bg, #fff6e6);
    color: var(--warn, #8a4b00);
  }

  .pill.saving {
    background: var(--surface, #fff);
    color: var(--fg-muted, #3f4750);
  }

  .banner {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin: 0.5rem;
    padding: 0.5rem 0.625rem;
    border-radius: 0.5rem;
    font-size: 0.875rem;
    font-weight: 600;
  }

  .banner.bad {
    background: var(--bad-bg, #fdecea);
    color: var(--bad, #b3261e);
  }

  .body {
    display: flex;
    flex-direction: column;
    gap: 0.625rem;
    padding: 0.75rem 0.625rem;
  }

  .times {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(9rem, 1fr));
    gap: 0.5rem;
  }

  .text {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    min-width: 0;
  }

  .text > span:first-child,
  .text > label {
    font-size: 0.8125rem;
    font-weight: 600;
    color: var(--fg-muted, #3f4750);
  }

  .withbtn {
    display: flex;
    gap: 0.25rem;
  }

  .text input {
    box-sizing: border-box;
    width: 100%;
    /* 16px minimum so iOS Safari does not zoom on focus. */
    font-size: 1rem;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    min-height: 3rem;
    padding: 0.5rem;
    border: 2px solid var(--border, #9aa3ad);
    border-radius: 0.5rem;
    background: var(--surface, #fff);
    color: var(--fg, #10151b);
  }

  .text input:focus {
    outline: none;
    border-color: var(--accent, #0b63c5);
    box-shadow: 0 0 0 3px var(--accent-ring, rgba(11, 99, 197, 0.28));
  }

  .quick {
    flex: 0 0 auto;
    min-height: 3rem;
    min-width: 3rem;
    padding: 0 0.5rem;
    font: inherit;
    font-size: 0.875rem;
    font-weight: 800;
    border: 2px solid var(--accent, #0b63c5);
    border-radius: 0.5rem;
    background: var(--accent-bg, #e8f1fb);
    color: var(--accent, #0b63c5);
    cursor: pointer;
    touch-action: manipulation;
  }

  .advice {
    margin: 0;
    padding: 0.5rem 0.625rem;
    border-left: 4px solid var(--accent, #0b63c5);
    background: var(--accent-bg, #e8f1fb);
    font-size: 0.875rem;
    font-weight: 600;
    color: var(--fg, #10151b);
  }

  .advice.warn {
    border-left-color: var(--bad, #b3261e);
    background: var(--bad-bg, #fdecea);
    color: var(--bad, #b3261e);
    font-weight: 700;
  }

  .rowactions {
    display: flex;
    gap: 0.5rem;
  }

  .quiet,
  .danger,
  .retry {
    min-height: 2.75rem;
    padding: 0 0.875rem;
    font: inherit;
    font-weight: 800;
    border-radius: 0.5rem;
    cursor: pointer;
    touch-action: manipulation;
  }

  .retry {
    min-height: 2.5rem;
    border: 2px solid currentColor;
    background: transparent;
    color: inherit;
  }

  .quiet {
    border: 2px solid var(--border, #9aa3ad);
    background: transparent;
    color: var(--fg-muted, #3f4750);
  }

  .danger {
    border: 2px solid var(--bad, #b3261e);
    background: var(--bad, #b3261e);
    color: #fff;
  }
</style>
